/** Sending files: the whole of the upload act, as one hook - moved out of the old Evidence screen
 * unchanged in what it asks and what it refuses (ADR-0212 moves it to the Upload place).
 *
 * Everything the old screen's panel did is here and nothing is new: the kind and the optional site
 * hint, the browser-side size check against the limit the **server** reports (ADR-0058, no copy of
 * the number lives in the SPA), one file at a time (the upload path's progress, duplicate and
 * interruption states are single slots, so several files are a queue and not a parallel send), a row
 * in the session's batch for every file whatever happened to it, and the three different ways a send
 * can stop: a document the server refused (`message`), a request that did not complete (`refusal`),
 * and a file turned away in the browser before anything was sent (`oversize`).
 *
 * **The batch is kept across a visit to the review screen and back, and nowhere else** (a
 * module-level map keyed by workspace and person - see `sessionBatch.ts`), so the row a person came
 * from is still there when they return.
 */

import { useEffect, useRef, useState } from "react";

import { useMe } from "../../api/auth";
import {
  DuplicateUploadError,
  UploadInterrupted,
  useFacilities,
  useUpload,
  useUploadLimit,
  useUploadPreCheck,
} from "../../api/evidence";
import type { UploadProgress } from "../../api/evidence";
import { useOrganization } from "../../api/queries";
import { isTabularContentType } from "../../lib/documents";
import { useLeavingWarning } from "../../lib/useLeavingWarning";
import type { BatchEntry } from "./BatchTable";
import { nextBatchKey, restoreBatch, saveBatch } from "./sessionBatch";

/** The roles that may add a document - mirrored from `evidence.py`'s `MAY_UPLOAD` (F-005). A mirror
 * and not an authority: the server refuses, and this only decides what to render. A list rather than
 * `role !== "viewer"` so a sixth role added to the API defaults to *not shown the control*. */
export const MAY_UPLOAD: readonly string[] = ["admin", "approver", "contributor"];

/** What the server took from the last batch: how many, the last one's name, and how many of them are
 * spreadsheets (which are imported rather than read, so their next step is worded differently). */
export interface Received {
  count: number;
  lastName: string;
  tabular: number;
}

export interface Interrupted {
  file: File;
  error: UploadInterrupted;
  allowDuplicate: boolean;
}

export function useSendFiles() {
  const upload = useUpload();
  const { data: me } = useMe();
  //: ADR-0101. `false` while the organisation read is in flight or has failed - the restriction is
  //: the default, so an absent answer never reads as permission.
  const { data: organization } = useOrganization();
  const realDocumentsPermitted = organization?.real_documents_permitted === true;
  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not": the controls are
  //: withheld until the answer is known, and nothing is asserted in the meantime.
  const mayUpload = me ? MAY_UPLOAD.includes(me.role) : undefined;
  const limit = useUploadLimit();
  const preCheck = useUploadPreCheck();
  const siteList = useFacilities(mayUpload === true);

  const [kind, setKind] = useState("electricity_bill");
  //: The site the next file is about, **as the uploader says** (a hint: the reviewer still chooses
  //: the meter). Empty is allowed and is the default.
  const [siteHint, setSiteHint] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [oversize, setOversize] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ file: File; existingId: string } | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [interrupted, setInterrupted] = useState<Interrupted | null>(null);
  //: **What the server took in the batch being sent** (Q-07). Files it refused are not here: they are
  //: said by `message`, and a "Received" over a refusal would be the confirmation that lies. Reset
  //: when a new batch is chosen, so the sentence is always about the last thing the person did.
  const [received, setReceived] = useState<Received | null>(null);

  //: Files chosen and not yet finished (being sent, or queued behind the one that is). While there
  //: are any, closing or reloading the tab says so first: the sends are made from this page.
  const [waiting, setWaiting] = useState(0);
  useLeavingWarning(waiting > 0 || upload.isPending);

  const batchKey = me ? `${me.org_id}:${me.user_id}` : null;
  const [batch, setBatch] = useState<BatchEntry[]>(() => (batchKey ? restoreBatch(batchKey) : []));
  useEffect(() => {
    if (batchKey) saveBatch(batchKey, batch);
  }, [batch, batchKey]);
  //: One handle per file for life, so a retry updates the row it belongs to.
  const retryKeys = useRef(new WeakMap<File, string>());
  const noteBatch = (entryKey: string, patch: Partial<BatchEntry>, fresh?: BatchEntry) =>
    setBatch((current) => {
      if (current.some((entry) => entry.key === entryKey)) {
        return current.map((entry) => (entry.key === entryKey ? { ...entry, ...patch } : entry));
      }
      return fresh ? [{ ...fresh, ...patch }, ...current] : current;
    });

  async function onUpload(file: File, allowDuplicate = false, resumeIntentId?: string) {
    setMessage(null);
    setRefusal(null);
    setDuplicate(null);
    setOversize(null);
    setInterrupted(null);
    const entryKey = retryKeys.current.get(file) ?? nextBatchKey();
    retryKeys.current.set(file, entryKey);
    noteBatch(
      entryKey,
      { status: "sending", note: undefined },
      { key: entryKey, name: file.name, size: file.size, kind, status: "sending" },
    );
    //: **Before the request**: the cheapest place to say no is a refusal made before a large file is
    //: read off the disk, and it is honest only because it reads the limit from the server. A retry
    //: that only confirms skips it: the file is already there.
    if (!resumeIntentId) {
      const tooLarge = await preCheck(file);
      if (tooLarge) {
        setOversize(`${tooLarge.lead} Nothing was sent. ${tooLarge.remedy}`);
        noteBatch(entryKey, { status: "failed", note: `${tooLarge.lead} ${tooLarge.remedy}` });
        return;
      }
    }
    try {
      const result = await upload.mutateAsync({
        file,
        kind,
        facilityId: siteHint || undefined,
        allowDuplicate,
        resumeIntentId,
        onProgress: setProgress,
      });
      noteBatch(entryKey, {
        status: result.accepted ? "sent" : "refused",
        documentId: result.document.id,
      });
      if (result.accepted) {
        setReceived((now) => ({
          count: (now?.count ?? 0) + 1,
          lastName: file.name,
          tabular: (now?.tabular ?? 0) + (isTabularContentType(result.document.content_type) ? 1 : 0),
        }));
      }
      if (!result.accepted) {
        // Reason then remedy: what happened, then what to do about it (DOC-01).
        setMessage(
          [result.document.state_reason ?? "That document was refused.", result.document.refusal_remedy]
            .filter(Boolean)
            .join(" "),
        );
      }
    } catch (error) {
      if (error instanceof DuplicateUploadError) {
        setDuplicate({ file, existingId: error.existingDocumentId });
        noteBatch(entryKey, { status: "duplicate", existingId: error.existingDocumentId });
        return;
      }
      if (error instanceof UploadInterrupted) {
        setInterrupted({ file, error, allowDuplicate });
        noteBatch(entryKey, { status: "interrupted", note: error.message });
        return;
      }
      // A refusal from the server: retrying it is useless, and `WriteFailed` says so.
      setRefusal(error);
      noteBatch(entryKey, {
        status: "failed",
        note: error instanceof Error ? error.message : "EmitIQ refused it.",
      });
    } finally {
      setProgress(null);
    }
  }

  /** Send files one after the other. */
  async function onFiles(files: File[]) {
    // Counted from the moment they are chosen, not from the moment each one starts: a file waiting
    // its turn is as lost as the one being sent if the tab is closed (G-P9-05).
    setWaiting((count) => count + files.length);
    setReceived(null);
    for (const file of files) {
      try {
        await onUpload(file);
      } finally {
        setWaiting((count) => count - 1);
      }
    }
  }

  return {
    me,
    mayUpload,
    realDocumentsPermitted,
    limit,
    siteList,
    kind,
    setKind,
    siteHint,
    setSiteHint,
    message,
    refusal,
    setRefusal,
    oversize,
    duplicate,
    progress,
    interrupted,
    received,
    batch,
    sending: upload.isPending,
    onFiles,
    onUpload,
  };
}
